import React from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'ts-debounce';

import { isMobile } from 'shared/util/mobile';
import { disableBodyScroll, enableBodyScroll } from 'shared/util/bodyScroll';
import { popURLContext, pushURLContext } from '@/lib/utils/mobileUrlHandler';
import { disableInputsExcept, enableInputs } from '@/lib/utils/disableInputs';

import { SpinnerIcon } from 'shared/icons/SpinnerIcon';
import styles from './autocomplete.module.css';

const fetchDebounceDelay = 200;

export class Autocomplete extends React.Component {
    constructor(props) {
        super(props);

        this.overlayParam = `?autocomplete=${this.props.id}`;
        this.state = {
            options: [],
            selectedOption: 0,
            focus: typeof window !== 'undefined' && window.location.search === this.overlayParam,
            fetching: false,
            clearOverlayInput: false,
            displayEmptyResultPlaceholder: false,
        };

        this.assetUrl = props.assetUrl?.endsWith('/') ? props.assetUrl : `${props.assetUrl}/`;
        this.fetchCount = 0;
        this.isMobile = isMobile();

        this.selectCurrentOption = () => {
            if (this.state.selectedOption >= 0 && this.state.selectedOption < this.state.options.length) {
                this.onClickOption(this.state.options[this.state.selectedOption]);
            } else {
                this.setState({ focus: false });
            }
        };

        this.fetchOptions = debounce((value) => {
            const fetchNum = ++this.fetchCount;

            this.setState({ fetching: true, focus: true });

            const encodedValue = encodeURIComponent(value);
            fetch(`${this.props.baseUrl}${encodedValue}`)
                .then((response) => response.json())
                .then((json) => {
                    if (fetchNum === this.fetchCount) {
                        const validResult = Array.isArray(json) && json.length;
                        const displayEmptyResultPlaceholder = !validResult && this.props.emptyResultPlaceholder;
                        const options = validResult ? this.props.mapResults(json) : this.state.options;
                        this.setState({
                            options: displayEmptyResultPlaceholder ? [] : options,
                            selectedOption: this.isMobile ? -1 : 0,
                            fetching: false,
                            displayEmptyResultPlaceholder,
                        });
                    }
                })
                .catch((error) => {
                    console.error(error);
                    if (fetchNum === this.fetchCount) {
                        this.setState({ fetching: false });
                    }
                });
        }, fetchDebounceDelay);

        this.prefetchOptions = debounce((value) => {
            const fetchNum = ++this.fetchCount;

            fetch(`${this.props.baseUrl}${value}`)
                .then((r) => r.json())
                .then((r) => {
                    if (fetchNum === this.fetchCount) {
                        const options = Array.isArray(r) && r.length ? this.props.mapResults(r) : this.state.options;
                        this.props.onChange(options[0]);
                    }
                })
                .catch();
        }, 10);

        this.onChange = ({ target: { value } }) => {
            this.props.onChangeText(value);
            this.setState({ focus: true, clearOverlayInput: false });

            if (value.length >= this.props.minChars) {
                this.fetchOptions(value);
            } else {
                this.setState((s) => ({
                    options: this.isMobile ? s.options : [],
                    fetching: false,
                    displayEmptyResultPlaceholder: false,
                }));
            }
        };

        this.onClickOption = (option) => {
            this.props.onChange(option);
            enableInputs();
            if (this.isMobile) {
                popURLContext(this.overlayParam);
            }
            const options = this.props.emptyOptionsOnChange ? [] : this.state.options;
            this.setState({ focus: false, clearOverlayInput: false, options }, enableBodyScroll);
        };

        this.keyDown = (e) => {
            switch (e.key) {
                case 'ArrowDown':
                    this.setState((s) => {
                        const selectedOption = (s.selectedOption + 1) % s.options.length;
                        return { selectedOption };
                    });
                    e.preventDefault();
                    break;

                case 'ArrowUp':
                    this.setState((s) => {
                        const selectedOption = s.selectedOption <= 0 ? s.options.length - 1 : s.selectedOption - 1;
                        return { selectedOption };
                    });
                    e.preventDefault();
                    break;

                case 'Enter':
                    this.selectCurrentOption();
                    e.preventDefault();
                    break;

                case 'Tab':
                    this.selectCurrentOption();
                    break;

                case 'Escape':
                    this.setState({ options: [], selectedOption: 0 });
                    break;
            }
        };

        this.onFocus = (event) => {
            if (this.props.onFocus) {
                this.props.onFocus();
            }
            disableBodyScroll();
            event.target.select();
            disableInputsExcept(this.props.id);
            if (this.isMobile) {
                pushURLContext(this.overlayParam);
            } else {
                window.addEventListener('keydown', this.keyDown);
            }
            this.setState({ focus: true, clearOverlayInput: false });
        };

        this.onBlur = () => {
            if (!this.isMobile) {
                window.removeEventListener('keydown', this.keyDown);
                this.selectCurrentOption();
            }
        };

        this.transitionOverlay = () => {
            if (this.isMobile) {
                if (window.history.state == null) {
                    // This means it's page load
                    return;
                }
                this.setState({ focus: window.location.search === this.overlayParam });
                if (!(window.location.search === this.overlayParam)) {
                    enableInputs();
                }
            }
        };
    }

    componentDidMount() {
        if (this.props.preFill != null) {
            this.prefetchOptions(this.props.preFill);
        }

        window.addEventListener('popstate', this.transitionOverlay);
    }

    componentWillUnmount() {
        this.fetchCount++; // Keep fetch requests that finish after the component has been unmounted from calling this.setState

        window.removeEventListener('popstate', this.transitionOverlay);
        window.removeEventListener('keydown', this.keyDown);
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        this.setState({ value: newProps.value });
    }

    onClear() {
        if (this.isMobile) {
            this.setState({ clearOverlayInput: true }, () => {
                this.inputMobile.focus();
            });
        } else {
            this.props.onChangeText('');
        }
    }

    onCancel() {
        this.setState({ focus: false }, () => {
            if (this.state.selectedOption === -1) {
                this.props.onChange();
            }
            enableInputs();
            enableBodyScroll();
            if (this.isMobile) {
                popURLContext(this.overlayParam);
            }
        });
    }

    renderOptions() {
        const getClicker = (option) => (e) => {
            e.stopPropagation();
            this.onClickOption(option);
        };
        const getMouseOverer = (optionIdx) => () => this.setState({ selectedOption: optionIdx });
        let lastOptionType = '';

        if (this.state.options.length > 0 && this.state.focus) {
            return (
                <ul role={this.isMobile ? undefined : 'listbox'} id={`${this.props.id}-options`} data-testid={`${this.props.id}-options`}>
                    {this.state.options.map((option, optionIdx) => {
                        let typeLabel = null;
                        let typeLabelLogo = null;
                        const cls = option.cls ? `${option.cls} ` : '';
                        if (
                            typeof option.type === 'string' &&
                            option.type.length > 0 &&
                            (option.type !== lastOptionType || optionIdx === 0)
                        ) {
                            const typeLabelLogoImg = `${this.assetUrl}${option.typeLogo}`;
                            const typeLabelImgCls = 'type-logo u-float-right u-pt2';
                            lastOptionType = option.type;
                            typeLabelLogo = option.typeLogo ? (
                                <img className={typeLabelImgCls} aria-hidden src={typeLabelLogoImg} alt="Logo" />
                            ) : null;
                            typeLabel = (
                                <li className="type u-t4 u-stone u-mh0 u-mv8">
                                    {typeLabelLogo} {lastOptionType}
                                </li>
                            );
                        }
                        return (
                            <React.Fragment key={`el${option.id}`}>
                                {typeLabel}
                                <li
                                    role={this.isMobile ? undefined : 'none'}
                                    className={`${cls}${this.state.selectedOption === optionIdx ? 'selected' : ''}`}
                                    onClick={getClicker(option)}
                                    onMouseOver={getMouseOverer(optionIdx)}>
                                    <a
                                        role={this.isMobile ? undefined : 'option'}
                                        aria-selected={this.isMobile ? undefined : this.state.selectedOption === optionIdx}
                                        id={`${this.props.id}-option${option.id}`}
                                        // biome-ignore lint/a11y/useValidAnchor: This was set before biome was added
                                        onClick={getClicker(option)}>
                                        {option.text}
                                    </a>
                                    {option.count === undefined ? null : <span className="u-float-right">{option.count}</span>}
                                </li>
                            </React.Fragment>
                        );
                    })}
                </ul>
            );
        }
        if (this.state.displayEmptyResultPlaceholder && this.state.focus) {
            return (
                <ul role={this.isMobile ? undefined : 'listbox'} id={`${this.props.id}-options`} data-testid={`${this.props.id}-options`}>
                    <li className="type u-stone u-mh0 u-mt8 u-mb2">{this.props.emptyResultPlaceholder}</li>
                </ul>
            );
        }
        return null;
    }

    renderSpinner() {
        return (
            <div className={`input-spinner ${this.state.focus && this.state.fetching ? 'active' : 'inactive'}`}>
                <SpinnerIcon />
            </div>
        );
    }

    render() {
        const { id, value, label, placeholder, errorText } = this.props;
        const errorClass = errorText ? ' input--is-invalid' : '';
        const platformClass = this.isMobile ? ' mobile' : ' desktop';
        const hasSelectedOption = this.state.options.length > 0 && this.state.focus && this.state.selectedOption !== -1;
        const hasText = value && value.length > 0;

        return (
            <div className={`${styles.Autocomplete} input input--text autocomplete${platformClass}${errorClass}`}>
                <label htmlFor={id} className="u-truncate">
                    {label}
                </label>
                <div
                    {...(!this.isMobile && {
                        role: 'combobox',
                        'aria-owns': `${id}-options`,
                        'aria-expanded': hasSelectedOption,
                    })}>
                    <input
                        type="text"
                        id={id}
                        className={`input-bg u-size1of1 u-truncate ${hasText ? ' u-pr32' : ''}`}
                        value={value}
                        data-testid={id}
                        autoComplete="off"
                        placeholder={placeholder || ''}
                        onChange={this.onChange}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                        ref={(field) => {
                            this.input = field;
                            return field;
                        }}
                        {...(!this.isMobile && {
                            'aria-autocomplete': 'list',
                            'aria-activedescendant': hasSelectedOption
                                ? `${id}-option${this.state.options[this.state.selectedOption].id}`
                                : undefined,
                            'aria-controls': hasSelectedOption ? `${id}-options` : undefined,
                        })}
                    />
                    {hasText && !this.isMobile ? (
                        <button
                            type="button"
                            aria-label="Nullstill"
                            data-testid="resetAutocompleteButton"
                            className="input--search__button input--search__button--reset"
                            onClick={this.onClear.bind(this)}
                        />
                    ) : null}
                </div>
                {errorText ? (
                    <div className="input__sub-text" role="alert">
                        <span className="input__sub-text__error">{errorText}</span>
                    </div>
                ) : null}
                {this.state.focus && this.isMobile ? (
                    <div className="u-pa16 overlay">
                        <div className="top-row">
                            <div className="input input--text u-float-left input-wrapper">
                                <label htmlFor={`${id}-mobile-overlay`} className="u-screen-reader-only">
                                    {label}
                                </label>
                                <input
                                    type="text"
                                    id={`${id}-mobile-overlay`}
                                    className="u-size1of1 u-pr32 u-truncate"
                                    value={this.state.clearOverlayInput ? '' : value}
                                    placeholder={placeholder ? placeholder : label}
                                    data-testid={`${id}-mobile-overlay`}
                                    autoComplete="off"
                                    onChange={this.onChange}
                                    ref={(field) => {
                                        this.inputMobile = field;
                                        return field;
                                    }}
                                />
                                <button
                                    type="button"
                                    aria-label="Nullstill"
                                    data-testid="resetAutocompleteButton"
                                    className="input--search__button input--search__button--reset"
                                    onClick={this.onClear.bind(this)}
                                />
                                {this.renderSpinner()}
                            </div>
                            <button
                                type="button"
                                aria-label="Avbryt"
                                className="input--search__submit pad-override button button--flat u-float-right"
                                data-testid="cancelAutocompleteButton"
                                onClick={this.onCancel.bind(this)}>
                                Avbryt
                            </button>
                        </div>
                        {this.renderOptions()}
                    </div>
                ) : (
                    <>
                        {this.renderOptions()}
                        {this.renderSpinner()}
                    </>
                )}
            </div>
        );
    }
}

Autocomplete.propTypes = {
    id: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    baseUrl: PropTypes.string.isRequired, // Requests will be made to `${baseUrl}${value}`
    assetUrl: PropTypes.string.isRequired,
    mapResults: PropTypes.func.isRequired, // Receives result of requests, should return and array of options
    onChange: PropTypes.func.isRequired, // Receives the selected option exactly as returned from mapResults
    onChangeText: PropTypes.func.isRequired, // Must update the value or the field will be read only
    onFocus: PropTypes.func,

    label: PropTypes.string,
    placeholder: PropTypes.string,
    minChars: PropTypes.number, // Minimum characters required to trigger a request
    errorText: PropTypes.string,
    preFill: PropTypes.string,
    emptyResultPlaceholder: PropTypes.string,
    emptyOptionsOnChange: PropTypes.bool,
};

Autocomplete.defaultProps = {
    label: '',
    minChars: 1,
    errorText: '',
    preFill: null,
    emptyOptionsOnChange: false,
};
